import { Component, useMemo, type FC } from 'react';
import { ILobbyState } from '../../../Core/State/ILobbyState';
import { IRoomState } from '../../../Core/State/IRoomState';
import { IUserState } from '../../../Core/State/IUserState';
import { IJoinedRoomState } from '../../../Core/State/IJoinedRoomState';
import { AppSections } from '../../../Core/State/AppSections';
import { t } from '../../../i18n';
import styles from '../../../assets/css/components/Lobby.module.css';
import { Box, Stack, ThemeProvider, Typography, createTheme, Tooltip, Button, Grid, Skeleton, Card, CardContent, CardActions, LinearProgress, SvgIcon, SvgIconProps, Divider } from '@mui/material';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { ITournamentState, ILobbyTournamentState } from '../../../Core/State/ILobbyTournamentState';
import { InteractionHandler } from '../../../Core/Handler/InteractionHandler';
import DetachedTournamentLobby from '../Tournament/Lobby/DetachedTournamentLobby';
import TournamentLobbyContent from '../Tournament/Lobby/TournamentLobbyContent';
import { buildHsUrlWithToken } from '../../../Utils/SessionUrlUtils';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import playersIcon from '/images/lobby/players.svg';
import dollarIcon from '/images/lobby/dollar.svg';
import giftIcon from '/images/lobby/gift.svg';
import trophyIcon from '/images/lobby/trophy-hero.svg';
import telegramIcon from '/images/lobby/telegram-icon.svg';
import telegramIconClean from '/images/lobby/telegram-icon-clean.svg';
import stickerIcon from '/images/lobby/sticker.svg';
import { useHomeTournamentSummary } from '../../../Tournament/TournamentSchedule';

import blackjackIcon from '/images/lobby/home/blackjack.png';
import slotsIcon from '/images/lobby/home/slots.png';
import predictIcon from '/images/lobby/home/predict.png';
import liveCasinoIcon from '/images/lobby/home/live-casino.png';
import crashIcon from '/images/lobby/home/crash.png';
import aviatorIcon from '/images/lobby/home/aviator.png';

import launchPartyPromo from '/images/lobby/home/launch-party.png';
import blackjackLiveDealerPromo from '/images/lobby/home/blackjack-live-dealer.png';
import pokerPromo from '/images/lobby/home/poker.png';
import MobileTableRow from '../Common/MobileTableRow';
import TournamentBox from '../Tournament/Lobby/TournamentBox';
import CryptoPrices from '../../CryptoPrices/CryptoPrices';
import GameSection from './GameSection';
import { highstakesGames, hacksawGames } from '../../../Utils/LobbyGameData';

interface DesktopLobbyProps {
  joinRoom: (roomId: string) => void;
  setSection: (section: string) => void;
  userState: IUserState;
  lobbyState: ILobbyState;
  lobbyTournamentState: ILobbyTournamentState;
  selectedRoomId: string | null;
  section: string;
  joinedRoomsState: IJoinedRoomState[];
  logout: () => void;
  userConnectedCount: number;
  interactionHandler: InteractionHandler;
}

interface DesktopLobbyState {
  selectedRoom: IRoomState | null;
  modalOpen: boolean;
  modalTournamentId: string | null;
  currentTime?: Date;
  currentPromoSlide: number;
}

function WheelIcon(props: SvgIconProps) {
    return (
      <SvgIcon {...props} viewBox="0 0 43 43">
        <g className={styles.wheelRotate} clipPath="url(#clip0_412_2036)">
            <path d="M13.0439 22.5979L0.523926 22.5979C0.762681 26.3506 2.25637 29.7618 4.59077 32.4165L13.4426 23.5647C13.2664 23.2576 13.1336 22.9325 13.0439 22.5979Z" fill="#882335"/>
            <path d="M6.09399 33.9197C8.74921 36.2545 12.1611 37.7483 15.9146 37.9866L15.9146 25.4671C15.5793 25.3774 15.2535 25.2445 14.9459 25.0679L6.09399 33.9197Z" fill="#F52740"/>
            <path d="M18.3527 20.158C17.973 19.7783 17.474 19.5884 16.9752 19.5884C16.4763 19.5884 15.9774 19.7782 15.5976 20.1581C14.8381 20.9177 14.8381 22.1536 15.5976 22.9132C16.3572 23.6727 17.5932 23.6728 18.3528 22.9132C19.1123 22.1535 19.1123 20.9176 18.3527 20.158Z" fill="#FECA9A"/>
            <path d="M13.4431 19.5055L4.59117 10.6536C2.25656 13.3081 0.762232 16.7192 0.523193 20.4718L13.0443 20.4718C13.1341 20.1374 13.2669 19.8124 13.4431 19.5055Z" fill="#F52740"/>
            <path d="M18.0409 25.4659L18.0409 37.9865C21.7927 37.7471 25.2028 36.2533 27.8569 33.9193L19.0052 25.0676C18.6989 25.2435 18.3746 25.376 18.0409 25.4659Z" fill="#882335"/>
            <path d="M6.09399 9.23993C8.74921 6.90518 12.1611 5.41135 15.9146 5.17302L15.9146 17.6925C15.5793 17.7823 15.2535 17.9152 14.9459 18.0918L6.09399 9.23993Z" fill="#882335"/>
            <path d="M18.0409 17.694L18.0409 5.17337C21.7927 5.41283 25.2028 6.90659 27.8569 9.24063L19.0052 18.0923C18.6989 17.9164 18.3746 17.7839 18.0409 17.694Z" fill="#F52740"/>
            <path d="M20.5079 23.5638L29.3599 32.4158C31.6939 29.7612 33.1874 26.3503 33.4261 22.5979L20.9062 22.5979C20.8165 22.9322 20.6839 23.257 20.5079 23.5638Z" fill="#F52740"/>
            <path d="M20.9059 20.4718L33.4269 20.4718C33.188 16.7195 31.6939 13.3086 29.3596 10.6543L20.5074 19.5064C20.6835 19.813 20.816 20.1377 20.9059 20.4718Z" fill="#882335"/>
        </g>
        <path className="indicator" d="M34.8312 21.5848L42.568 24.896L42.568 18.2644L34.8312 21.5848Z" fill="#FECA9A"/>
        <defs>
            <clipPath id="clip0_412_2036">
                <rect width="42" height="42" fill="white" transform="translate(0.567993 0.580078)"/>
            </clipPath>
        </defs>
      </SvgIcon>
    );
  }

class DesktopHomeLobbyView extends Component<DesktopLobbyProps, DesktopLobbyState> {
  timerId?: NodeJS.Timeout;
  promoCarouselTimerId?: NodeJS.Timeout;
  private lastRoomsRef?: IRoomState[];
  private cachedAofTables?: IRoomState[];
  private cachedCashTables?: IRoomState[];

  constructor(props: DesktopLobbyProps) {
    super(props);
    this.state = {
      selectedRoom: null,
      modalOpen: false,
      modalTournamentId: null,
      currentTime: new Date(),
      currentPromoSlide: 0
    };
  }

  handleClick = (gameId: string) => {
    this.setState({ modalTournamentId: gameId, modalOpen: true });
  };

  handleModalClose = () => {
    this.setState({ modalOpen: false, modalTournamentId: null });
  };

  handleRowClick = (_params: GridRowParams<ITournamentState>) => {
    // For now, just handle double click
    // In the future, could show tournament details on the side
  };

  handleRowDoubleClick = (params: GridRowParams<ITournamentState>) => {
    this.setState({ modalTournamentId: params.row.gameId, modalOpen: true });
  };

  darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#F93751',
      },
    },
    typography: {
      fontFamily: 'Inter, serif',
    },
  });

  componentDidMount(): void {
    this.timerId = setInterval(() => {
      this.setState({ currentTime: new Date() });
    }, 1000);
  }

  handlePromoIndicatorClick = (slideIndex: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent event from bubbling to parent Box onClick
    this.setState({ currentPromoSlide: slideIndex });
  }

  componentWillUnmount(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    if (this.promoCarouselTimerId) {
      clearInterval(this.promoCarouselTimerId);
    }
  }

  componentDidUpdate(prevProps: DesktopLobbyProps) {
    if (this.state.selectedRoom && prevProps.lobbyState !== this.props.lobbyState) {
      const updatedRoom = this.props.lobbyState.rooms.find(
        (room) => room.roomId === this.state.selectedRoom?.roomId,
      );
      if (updatedRoom) {
        this.setState({ selectedRoom: updatedRoom });
      }
    }
  }

  render() {
    const user = this.props.userState;

    const rooms = this.props.lobbyState.rooms;
    if (this.lastRoomsRef !== rooms) {
      this.lastRoomsRef = rooms;

      // Retrieve aof poker tables
      this.cachedAofTables = rooms
        .filter((table) => table.type === 'aof')
        .filter((table) => table.players.length < table.maxPlayers)
        .sort((a, b) => b.players.length - a.players.length)
        .slice(0, 6);

      // Retrieve hold'em poker tables
      this.cachedCashTables = rooms
        .filter((table) => table.type === 'texas')
        .filter((table) => table.players.length < table.maxPlayers)
        .sort((a, b) => b.players.length - a.players.length)
        .slice(0, 4);
    }

    // Retrieve aof poker tables
    const aofTables = this.cachedAofTables || [];

    // Retrieve hold'em poker tables
    const cashTables = this.cachedCashTables || [];

    const registeredTournaments = this.props.lobbyTournamentState?.registeredTournaments || [];
    const upcomingTournaments = this.props.lobbyTournamentState?.upcomingTournaments || [];
    const registeredTournamentIds = new Set(
      registeredTournaments
        .map((tournament) => tournament.gameId || tournament.tournamentId)
        .filter(Boolean)
    );
    const homeTournaments = [
      ...registeredTournaments,
      ...upcomingTournaments.filter((tournament) => !registeredTournamentIds.has(tournament.gameId || tournament.tournamentId))
    ];

    const modalTournament = this.state.modalTournamentId
      ? homeTournaments.find((t: ITournamentState) => t.gameId === this.state.modalTournamentId || t.tournamentId === this.state.modalTournamentId) || null
      : null;

    const cashGameColumns: GridColDef<IRoomState>[] = [
        { field: 'name', headerName: t('DesktopHomeLobby:columns.table'), headerClassName: styles.tableHeader, flex: 2, type: 'string', resizable: false },
        { field: 'type', headerName: t('DesktopHomeLobby:columns.game'), headerClassName: styles.tableHeader, flex: 1, type: 'string', resizable: false,
            valueGetter: (params: any) => {
                switch (params.value.toLowerCase()) {
                    case 'texas':
                        return t('DesktopHomeLobby:gameTypes.holdem');
                    case 'aof':
                        return t('DesktopHomeLobby:gameTypes.aof');
                    default:
                        return params.value.charAt(0).toUpperCase() + params.value.slice(1);
                }
            },
            renderCell: (params) => {
                const value = params.value?.toLowerCase();

                switch (value) {
                    case 'texas':
                        return (
                            <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                {params.value}
                            </Box>
                        );
                    case 'aof':
                        return (
                            <Box className={styles.typeLabel} sx={{ backgroundColor: '#57222E', color: '#FC5468' }}>
                                {params.value}
                            </Box>
                        );
                    default:
                        return (
                            <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                {params.value}
                            </Box>
                        );
                }
            }
        },
        { field: 'stakes', headerName: t('DesktopHomeLobby:columns.stakes'), headerClassName: styles.tableHeader, flex: 1, type: 'string', resizable: false, valueGetter: (params: any) => `$${params.row.smallBlind} / $${params.row.bigBlind}` },
        { field: 'maxBuyIn', headerName: t('DesktopHomeLobby:columns.buyIn'), headerClassName: styles.tableHeader, flex: 1, type: 'string', resizable: false,
            renderCell: (params: any) => {
                const formatCurrency = (value: number) => {
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                        maximumFractionDigits: 2
                    }).format(value);
                };
                return (
                    <Tooltip title={
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold', marginBottom: '0.125rem' }}>{t('DesktopHomeLobby:tooltips.buyIn')}</span>
                            <span>{t('DesktopHomeLobby:tooltips.minBuyIn')}: {formatCurrency(params.row.minBuyIn)}</span>
                            <span>{t('DesktopHomeLobby:tooltips.maxBuyIn')}: {formatCurrency(params.row.maxBuyIn)}</span>
                        </Box>
                    } placement="top" arrow>
                        <span>{`${formatCurrency(params.row.minBuyIn)} / ${formatCurrency(params.row.maxBuyIn)}`}</span>
                    </Tooltip>
                );
            }
        },
        { field: 'seats', headerName: t('DesktopHomeLobby:columns.players'), headerClassName: styles.tableHeader, flex: 1, type: 'number', resizable: false, valueGetter: (params: any) => `${Math.min(params.row.players.length, params.row.maxPlayers)} / ${params.row.maxPlayers}`, align: 'left', headerAlign: 'left', renderCell: (params) => {
            const { playerSatCount, minPlayers, maxPlayers } = params.row;

            let textColor = '#179F55';
            let tooltip = t('DesktopHomeLobby:tooltips.openSeats');

            if (playerSatCount < minPlayers) {
              textColor = '#FFC100';
              tooltip = t('DesktopHomeLobby:tooltips.needsPlayers', { count: minPlayers });
            } else if (playerSatCount === maxPlayers) {
              textColor = '#FF004D';
              tooltip = t('DesktopHomeLobby:tooltips.tableFull');
            }

            return (
                <Tooltip title={tooltip} placement="top" arrow>
                    <Box className={styles.playerCount} sx={{ '--color': textColor }}>
                        <img src={playersIcon} alt="Players Icon" style={{ width: 15, height: 13, marginRight: 4 }} />
                        {`${Math.min(params.row.players.length, params.row.maxPlayers)} / ${params.row.maxPlayers}`}
                    </Box>
                </Tooltip>
            );
        } },
        {
            field: 'join',
            headerName: '',
            headerClassName: styles.tableHeader,
            cellClassName: styles.lastTableCell,
            type: 'actions',
            flex: 1.5,
            resizable: false,
            renderCell: (params: any) => (
                <Button
                    variant="contained"
                    fullWidth
                    className={this.props.joinedRoomsState.some((room) => room.roomId === params.row.roomId) ? styles.openedButton : styles.joinButton}
                    style={{ marginRight: '1rem' }}
                    onClick={() => {
                        this.props.joinRoom(params.row.roomId);
                    }}
                >
                    {this.props.joinedRoomsState.some((room) => room.roomId === params.row.roomId) ? t('DesktopHomeLobby:buttons.tableOpened') : t('DesktopHomeLobby:buttons.openTable')}
                </Button>
            ),
            align: 'left',
            headerAlign: 'left',
        },
    ];

    const customDataGridClasses = {
        virtualScroller: styles.customScroller,
        columnHeaders: styles.tableHeaderRow,
        columnHeader: styles.tableHeaderCell,
        cell: styles.tableCell,
        row: styles.tableRow,
    };

    return (
        <ThemeProvider theme={this.darkTheme}>
            <div style={{ display: 'flex', width: '100%', height: 'calc(100dvh - 140px)', gap: '1rem', padding: '1rem 1rem 0 1rem', boxSizing: 'border-box' }}>
                <div className={styles.hideScrollbar} style={{ width: 'calc(100% - 1rem - 256px)', height: '100%', flexGrow: 0, flexShrink: 0, boxSizing: 'border-box', overflowY: 'auto', paddingBottom: '1rem' }}>
                    <Box sx={{ flexGrow: 1, mb: '1rem' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: '0.5rem' }}>
                            <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                                {t('DesktopHomeLobby:sections.upcomingTournaments')}
                            </Typography>
                        </Stack>
                        <Box sx={{
                            display: "flex",
                            overflowX: "auto",
                            overflowY: "hidden",
                            gap: 2,
                            p: 0,
                            scrollSnapType: "x mandatory",
                            scrollBehavior: "smooth",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            "&::-webkit-scrollbar": {
                                display: "none"
                            }
                        }}>
                            {homeTournaments.length === 0 ? (
                                // Show skeleton loaders while tournaments are loading
                                Array.from({ length: 5 }).map((_, index) => (
                                    <Card
                                        key={`skeleton-${index}`}
                                        sx={{
                                            minWidth: 'max(206px, calc((100% - 64px) / 5))',
                                            maxWidth: 'max(212px, calc((100% - 64px) / 5))',
                                        }}
                                    >
                                        <Box sx={{ p: 3, backgroundColor: '#1C1C25' }}>
                                            <Skeleton variant="rectangular" height={45} sx={{ bgcolor: '#2A2A32' }} />
                                        </Box>
                                        <CardContent sx={{ backgroundColor: '#2A2A32', py: 1, px: "11px" }}>
                                            <Skeleton variant="rounded" width={100} height={18} sx={{ bgcolor: '#3A3A42', mb: 1 }} />
                                            <Skeleton variant="text" width="80%" height={20} sx={{ bgcolor: '#3A3A42', mb: 1 }} />
                                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                                                <Skeleton variant="text" width={80} height={16} sx={{ bgcolor: '#3A3A42' }} />
                                                <Skeleton variant="text" width={70} height={16} sx={{ bgcolor: '#3A3A42' }} />
                                            </Stack>
                                        </CardContent>
                                        <CardActions sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#222328', height: '24px', px: "11px" }}>
                                            <Skeleton variant="text" width={60} height={14} sx={{ bgcolor: '#3A3A42' }} />
                                            <Skeleton variant="circular" width={19} height={19} sx={{ bgcolor: '#3A3A42' }} />
                                        </CardActions>
                                    </Card>
                                ))
                            ) : (
                                homeTournaments.map((tournament) => (
                                    <TournamentBox key={tournament.gameId || tournament.tournamentId} tournament={tournament} userState={this.props.userState} handleClick={this.handleClick} />
                                ))
                            )}
                        </Box>
                    </Box>
                    <Stack direction="row" justifyContent="start" alignItems="center" gap={2} sx={{ mb: '1rem' }}>
                        <Stack direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbyPromo} onClick={() => {}} title="Link to your website here">
                            <img src={blackjackIcon} alt="Blackjack Icon" width={42} height={42} style={{ marginRight: '0.5rem' }} />
                            <Typography style={{ fontSize: '12px', fontWeight: 900, color: '#FFFFFF', textTransform: 'uppercase' }}>Blackjack</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbyPromo} onClick={() => {}} title="Link to your website here">
                            <img src={slotsIcon} alt="Blackjack Icon" width={42} height={42} style={{ marginRight: '0.5rem' }} />
                            <Typography style={{ fontSize: '12px', fontWeight: 900, color: '#FFFFFF', textTransform: 'uppercase' }}>Slots</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbyPromo} onClick={() => {}} title="Link to your website here">
                            <img src={predictIcon} alt="Blackjack Icon" width={42} height={42} style={{ marginRight: '0.5rem' }} />
                            <Typography style={{ fontSize: '12px', fontWeight: 900, color: '#FFFFFF', textTransform: 'uppercase' }}>Predict</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbyPromo} onClick={() => {}} title="Link to your website here">
                            <img src={liveCasinoIcon} alt="Blackjack Icon" width={42} height={42} style={{ marginRight: '0.5rem' }} />
                            <Typography style={{ fontSize: '12px', fontWeight: 900, color: '#FFFFFF', textTransform: 'uppercase' }}>Live Casino</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbyPromo} onClick={() => {}} title="Link to your website here">
                            <img src={crashIcon} alt="Blackjack Icon" width={42} height={42} style={{ marginRight: '0.5rem' }} />
                            <Typography style={{ fontSize: '12px', fontWeight: 900, color: '#FFFFFF', textTransform: 'uppercase' }}>Crash</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbyPromo} onClick={() => {}} title="Link to your website here">
                            <img src={aviatorIcon} alt="Blackjack Icon" width={42} height={42} style={{ marginRight: '0.5rem' }} />
                            <Typography style={{ fontSize: '12px', fontWeight: 900, color: '#FFFFFF', textTransform: 'uppercase' }}>Aviator</Typography>
                        </Stack>
                    </Stack>
                    <GameSection
                        title={t('DesktopHomeLobby:sections.highstakesGames')}
                        games={highstakesGames}
                        containerClassName={styles.homeLobbyGameContainer}
                        gameBoxClassName={styles.homeLobbyGameBox}
                        thumbnailClassName={styles.homeLobbyGameBoxThumbnail}
                        titleClassName={styles.homeLobbyGameBoxTitle}
                    />
                    <GameSection
                        title={t('DesktopHomeLobby:sections.hacksawTopGames')}
                        games={hacksawGames}
                        containerClassName={styles.homeLobbyGameContainer}
                        gameBoxClassName={styles.homeLobbyGameBox}
                        thumbnailClassName={styles.homeLobbyGameBoxThumbnail}
                        titleClassName={styles.homeLobbyGameBoxTitle}
                    />
                    {aofTables.length > 0 && (
                        <Box sx={{ flexGrow: 1, mb: '1rem' }}>
                            <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '0.5rem' }}>{t('DesktopHomeLobby:sections.aofTables')}</Typography>
                            <Grid container spacing={2}>
                                {aofTables.map((table) => (
                                    <Grid item xs={4} key={table.roomId}>
                                        <MobileTableRow key={table.roomId} table={table} joinedRoomsState={this.props.joinedRoomsState} joinRoom={this.props.joinRoom} userState={this.props.userState} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                    <Stack direction="row" justifyContent="start" alignItems="center" gap={2} sx={{ mb: '1rem' }}>
                        <Stack
                            direction="row"
                            justifyContent="start"
                            alignItems="center"
                            className={styles.homeLobbyBigPromo}
                            sx={{ cursor: 'pointer' }}
                        >
                            <img src={launchPartyPromo} alt="Launch Party Promo" width={90} height={110} style={{ marginRight: '1rem' }} />
                            <Stack direction="column" justifyContent="start" alignItems="start">
                                <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>{t('DesktopHomeLobby:promo.launchParty')}</Typography>
                                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#F52740', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{t('DesktopHomeLobby:promo.monthlyGuarantees')}</Typography>
                                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#8695A6' }}>✅ {t('DesktopHomeLobby:promo.tournamentSchedule')}</Typography>
                            </Stack>
                        </Stack>
                        <Stack
                            direction="row"
                            justifyContent="start"
                            alignItems="center"
                            className={styles.homeLobbyBigPromo}
                            sx={{ cursor: 'pointer' }}
                        >
                            <img src={blackjackLiveDealerPromo} alt="Blackjack Promo" width={90} height={110} style={{ marginRight: '1rem' }} />
                            <Stack direction="column" justifyContent="start" alignItems="start">
                                <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>{t('DesktopHomeLobby:promo.blackjackLiveDealer')}</Typography>
                                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#F52740', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{t('DesktopHomeLobby:promo.dailyLeaderboard')}</Typography>
                                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#8695A6' }}>✅ {t('DesktopHomeLobby:promo.weeklyCashback')}</Typography>
                            </Stack>
                        </Stack>
                        <Stack
                            direction="row"
                            justifyContent="start"
                            alignItems="center"
                            className={styles.homeLobbyBigPromo}
                            sx={{ cursor: 'pointer' }}
                        >
                            <img src={pokerPromo} alt="Poker Promo" width={90} height={110} style={{ marginRight: '1rem' }} />
                            <Stack direction="column" justifyContent="start" alignItems="start">
                                <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>{t('DesktopHomeLobby:promo.pokerAnytime')}</Typography>
                                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#F52740', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{t('DesktopHomeLobby:promo.weeklyRakeback')}</Typography>
                                <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#8695A6' }}>✅ {t('DesktopHomeLobby:promo.platforms')}</Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                    {cashTables.length > 0 && (
                        <>
                            <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF', marginBottom: '0.5rem' }}>{t('DesktopHomeLobby:sections.holdemTables')}</Typography>
                            <DataGrid
                                disableColumnMenu
                                disableColumnFilter
                                getCellClassName={() => styles.tableCell}
                                getRowClassName={(params) => {
                                    const userId = user?.id;
                                    if (!userId) {
                                        return styles.tableRow;
                                    }
                                    const isUserPlaying = params.row.players.some((player: { userId: string }) => player.userId === user.id);
                                    return isUserPlaying ? styles.active : styles.tableRow;
                                }}
                                rows={cashTables}
                                columns={cashGameColumns}
                                classes={customDataGridClasses}
                                pageSizeOptions={[500]}
                                getRowId={(row) => row.roomId}
                                rowHeight={49}
                                hideFooter
                                autoHeight={true}
                                getRowSpacing={(params) => ({
                                    top: params.isFirstVisible ? 0 : 4,
                                    bottom: 0,
                                })}
                                onRowClick={this.handleRowClick}
                                slots={{
                                    columnSortedDescendingIcon: ArrowDropUp,
                                    columnSortedAscendingIcon: ArrowDropDown,
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: 'seats', sort: 'desc' }]
                                    }
                                }}
                                sx={{
                                    border: 'none',
                                    color: 'white',
                                    '& .MuiDataGrid-virtualScroller': {
                                        '&::-webkit-scrollbar': {
                                            width: '0.4em',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: 'transparent',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'rgba(255,255,255,.3)',
                                        },
                                        '&::-webkit-scrollbar-thumb:hover': {
                                            background: 'rgba(255,255,255,.5)',
                                        },
                                    },
                                    '& .MuiDataGrid-virtualScrollerRenderZone': {
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        overflow: 'hidden'
                                    },
                                    '& .MuiDataGrid-root': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        display: 'none !important',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        fontFamily: 'Inter, serif'
                                    },
                                    '& .MuiDataGrid-cell:first-of-type': {
                                        borderLeft: '2px solid #F52740 !important',
                                    },
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                        background: 'linear-gradient(90deg, rgba(245, 39, 64, 0.10) 0%, rgba(28, 28, 37, 0.10) 22.39%), #2A2A32',
                                        transition: 'background 300ms ease',
                                        width: '100%',
                                        boxSizing: 'border-box'
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer',
                                        background: '#40414C !important',
                                    },
                                    '& .MuiDataGrid-row:focus': {
                                        backgroundColor: '#40414C',
                                    },
                                    '& .MuiDataGrid-row:active': {
                                        backgroundColor: '#40414C',
                                    },
                                    '& .MuiDataGrid-sortIcon': {
                                        color: '#66666b',
                                        fontSize: '2rem'
                                    },
                                    '& .MuiDataGrid-row.Mui-selected': {
                                        backgroundColor: '#40414C',
                                    },
                                    '& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)': {
                                        display: 'none !important',
                                    },
                                }}
                            />
                        </>
                    )}
                </div>
                <div className={styles.hideScrollbar} style={{ display: 'flex', flexDirection: 'column', width: '256px', height: '100%', flexGrow: 0, flexShrink: 0, gap: '0.5rem', boxSizing: 'border-box', overflowY: 'auto', paddingBottom: '1rem' }}>
                    {/* Promos */}
                    <Stack direction="row" justifyContent="center" alignItems="center" p={1} className={styles.homeLobbySidebarPromo} onClick={() => window.open(buildHsUrlWithToken('/wheel'), '_blank')}>
                        <WheelIcon sx={{ width: 36, height: 36, marginRight: '0.5rem' }} />
                        <Typography style={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>{t('DesktopHomeLobby:promo.spinNowForFree')}</Typography>
                    </Stack>
                    <Stack spacing={1} my={1} style={{ cursor: 'pointer'}} onClick={() => window.open(buildHsUrlWithToken('/casino-race/all-race'), '_blank')}>
                        <Stack direction="row" justifyContent="space-between">
                            <Typography style={{ fontSize: '14px', fontWeight: 600, color: '#FFFFFF' }}>
                                <EmojiEventsIcon style={{ fontSize: '16px', color: '#FFFFFF', verticalAlign: 'middle' }} /> {t('DesktopHomeLobby:promo.dailyRace')}
                            </Typography>
                            <Typography style={{ fontSize: '14px', fontWeight: '600', color: '#3DE361'}}>24h</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={(() => {
                            const now = this.state.currentTime || new Date();
                            const seconds = now.getSeconds();
                            // Calculate progress: 100% at second 0 (60 seconds remaining), 0% at second 59 (1 second remaining)
                            // Formula: (60 - seconds) / 60 * 100
                            return ((60 - seconds) / 60) * 100;
                        })()} />
                    </Stack>
                    <Stack px={2} py={1.5} direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbySidebarPromo} onClick={() => window.open(buildHsUrlWithToken('/account/payment/deposit'), '_blank')}>
                        <Box className={styles.homeLobbySidebarPromoIcon}>
                            <img src={giftIcon} alt="Gift Icon" width={18} height={18} />
                        </Box>
                        <Stack>
                            <Typography style={{ fontSize: '14px', fontWeight: 700 }}>{t('DesktopHomeLobby:promo.depositBonusSmall')}</Typography>
                            <Typography style={{ fontSize: '12px', fontWeight: 600, color: '#7B7B7C' }}>{t('DesktopHomeLobby:promo.firstDepositX2')}</Typography>
                        </Stack>
                    </Stack>
                    <CryptoPrices />
                    <Divider sx={{ my: 2, borderWidth: '1px', borderColor: 'rgba(255,255,255,0.10)' }} />
                    <Box className={styles.homeLobbySidebarLabel} px={1} py={0.25}>
                        <Typography style={{ fontSize: '10px', fontWeight: '600', color: '#FFF0E3' }}>{t('DesktopHomeLobby:promo.highstakesTelegram')}</Typography>
                    </Box>
                    <Stack direction="row" p={2} className={styles.telegramPromo} justifyContent="center" alignItems="center" style={{ position: 'relative', height: '80px' }}>
                        <Typography style={{ fontSize: '16px', fontWeight: 800, color: '#FFF0E3', maxWidth: '70%' }}>{t('DesktopHomeLobby:promo.openTelegramApp')}</Typography>
                        <img src={telegramIcon} alt="PokerSolutions Merchandise" style={{ filter: 'drop-shadow(0 0 17px #2D74FD)' }}/>
                    </Stack>
                    <Stack p={1.5} direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbySidebarPromo}>
                        <Box className={styles.homeLobbySidebarPromoIcon}>
                            <img src={telegramIconClean} alt="Telegram Icon" width={18} height={18} />
                        </Box>
                        <Stack>
                            <Typography style={{ fontSize: '12px', fontWeight: 700 }}>{t('DesktopHomeLobby:promo.joinCommunity')}</Typography>
                            <Typography style={{ fontSize: '10px', fontWeight: 600, color: '#7B7B7C' }}>{t('DesktopHomeLobby:promo.joinTelegramCommunity')}</Typography>
                        </Stack>
                        <Box className={styles.homeLobbySidebarPromoButton}>
                            <ArrowForwardIcon sx={{ fontSize: '18px', verticalAlign: 'middle' }} />
                        </Box>
                    </Stack>
                    <Stack p={1.5} direction="row" justifyContent="start" alignItems="center" className={styles.homeLobbySidebarPromo}>
                        <Box className={styles.homeLobbySidebarPromoIcon}>
                            <img src={stickerIcon} alt="Sticker Icon" width={18} height={18} />
                        </Box>
                        <Stack>
                            <Typography style={{ fontSize: '12px', fontWeight: 700 }}>{t('DesktopHomeLobby:promo.exclusiveStickers')}</Typography>
                            <Typography style={{ fontSize: '10px', fontWeight: 600, color: '#7B7B7C' }}>{t('DesktopHomeLobby:promo.downloadStickers')}</Typography>
                        </Stack>
                        <Box className={styles.homeLobbySidebarPromoButton}>
                            <ArrowForwardIcon sx={{ fontSize: '18px', verticalAlign: 'middle' }} />
                        </Box>
                    </Stack>
                </div>
            </div>
            {this.state.modalOpen && modalTournament && (
                <DetachedTournamentLobby title={`Lobby for: ${modalTournament.name}`} onClose={this.handleModalClose}>
                    <TournamentLobbyContent tournament={modalTournament} userState={this.props.userState} interactionHandler={this.props.interactionHandler}></TournamentLobbyContent>
                </DetachedTournamentLobby>
            )}
        </ThemeProvider>
    );
  }
}

const DesktopHomeLobby: FC<DesktopLobbyProps> = (props) => {
  const tournamentSummary = useHomeTournamentSummary();
  const lobbyTournamentState = useMemo<ILobbyTournamentState>(() => ({
    ...props.lobbyTournamentState,
    tournaments: tournamentSummary.tournaments,
    upcomingTournaments: tournamentSummary.upcomingTournaments,
    registeredTournaments: tournamentSummary.registeredTournaments,
  }), [
    props.lobbyTournamentState,
    tournamentSummary.tournaments,
    tournamentSummary.upcomingTournaments,
    tournamentSummary.registeredTournaments,
  ]);

  return <DesktopHomeLobbyView {...props} lobbyTournamentState={lobbyTournamentState} />;
};

export default DesktopHomeLobby;
